import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { CalendarClock, CalendarDays, CalendarX, Plus } from "lucide-react"
import { useEffect, useMemo } from "react"
import { z } from "zod"

import { EventListFilters } from "@/components/guild-events-admin/event-list-filters"
import { EventListPagination } from "@/components/guild-events-admin/event-list-pagination"
import { EventListRow } from "@/components/guild-events-admin/event-list-row"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { buttonVariants } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { TablePageSkeleton } from "@/components/ui/page-skeletons"
import { type GuildEventRecord, guildEventsApi } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"

export const PAGE_SIZE_OPTIONS = [12, 24, 36] as const

const searchSchema = z.object({
  status: z
    .enum([
      "all",
      "scheduled",
      "closed",
      "cancelled",
      "started",
      "announcement_missing",
    ])
    .optional(),
  bucket: z.enum(["all", "active", "upcoming", "past"]).optional(),
  query: z.string().trim().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce
    .number()
    .int()
    .refine((n) => (PAGE_SIZE_OPTIONS as readonly number[]).includes(n))
    .optional(),
})

export const Route = createFileRoute("/_authed/_admin/guild-events/")({
  validateSearch: searchSchema,
  component: GuildEventsAdminListRoute,
})

function GuildEventsAdminListRoute() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const status = search.status ?? "all"
  const bucket = search.bucket ?? "active"
  const query = search.query ?? ""
  const page = search.page ?? 1
  const pageSize = search.pageSize ?? 12

  const pageQuery = useQuery({
    queryKey: guildEventsKeys.adminList({
      status,
      bucket,
      query,
      page,
      pageSize,
    }),
    queryFn: () =>
      guildEventsApi.listAdminEvents({ status, bucket, query, page, pageSize }),
    placeholderData: keepPreviousData,
  })
  const metricsQuery = useQuery({
    queryKey: guildEventsKeys.adminList({}),
    queryFn: () => guildEventsApi.listAdminEvents({}),
  })

  const pageCount = Math.max(
    1,
    Math.ceil((pageQuery.data?.total ?? 0) / pageSize),
  )
  const safePage = Math.min(page, pageCount)
  useEffect(() => {
    if (page !== safePage) {
      void navigate({
        search: (prev) => ({ ...prev, page: safePage }),
        replace: true,
      })
    }
  }, [page, safePage, navigate])

  const metrics = useMemo(
    () => deriveMetrics(metricsQuery.data?.items ?? []),
    [metricsQuery.data],
  )

  const handleNavigate = (
    updater: (prev: {
      status?: string
      bucket?: string
      query?: string
      page?: number
      pageSize?: number
    }) => Record<string, unknown>,
  ) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        ...updater(prev),
      }),
      replace: true,
    })
  }

  if (pageQuery.isLoading) {
    return <TablePageSkeleton />
  }

  if (pageQuery.isError) {
    return (
      <StatusAlert tone="danger">
        <div className="flex items-center justify-between gap-3">
          <span>Failed to load events.</span>
          <button
            type="button"
            className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
            onClick={() => void pageQuery.refetch()}
          >
            Retry
          </button>
        </div>
      </StatusAlert>
    )
  }

  const pageItems = pageQuery.data?.items ?? []

  return (
    <div className="grid gap-4 md:gap-5">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          kicker={
            <SectionKicker icon={<CalendarDays />}>Guild Events</SectionKicker>
          }
          title="Coordinate guild events from one control room"
          description="Create events, monitor signup health, and manage RSVP operations without Discord context switching."
          actions={
            <div className="flex items-center gap-2">
              <RefreshButton
                isFetching={pageQuery.isFetching || metricsQuery.isFetching}
                onClick={() => {
                  void pageQuery.refetch()
                  void metricsQuery.refetch()
                }}
                variant="outline"
              />
              <Link
                to="/guild-events/schedules"
                className={buttonVariants({ variant: "outline" })}
              >
                <CalendarClock className="size-4" />
                Schedules
              </Link>
              <Link
                to="/guild-events/archived"
                className={buttonVariants({ variant: "outline" })}
              >
                <CalendarX className="size-4" />
                Archived
              </Link>
              <Link
                to="/guild-events/new"
                className={buttonVariants({ variant: "default" })}
              >
                <Plus className="size-4" />
                Create Event
              </Link>
            </div>
          }
          metrics={
            <>
              <MetricTile label="Upcoming" value={String(metrics.upcoming)} />
              <MetricTile label="Scheduled" value={String(metrics.scheduled)} />
              <MetricTile label="Degraded" value={String(metrics.degraded)} />
            </>
          }
        />
      </Card>

      <EventListFilters
        status={status}
        bucket={bucket}
        query={query}
        pageSize={pageSize}
        onNavigate={handleNavigate}
      />

      <Card>
        <CardContent className="pt-5">
          {pageItems.length === 0 ? (
            <EmptyState
              icon={<CalendarX className="size-4" />}
              title={
                (metricsQuery.data?.items.length ?? 0) === 0
                  ? "No events yet"
                  : bucket === "past"
                    ? "No past events"
                    : status === "announcement_missing"
                      ? "No announcements need recovery"
                      : "No matching events"
              }
              description={
                (metricsQuery.data?.items.length ?? 0) === 0
                  ? "No guild events have been created yet."
                  : bucket === "past"
                    ? "There are no past events for the current filters."
                    : status === "announcement_missing"
                      ? "No events currently need announcement recovery."
                      : "No events match your current filters or search. Try adjusting them."
              }
              actions={
                <Link
                  to="/guild-events/new"
                  className={buttonVariants({ variant: "default", size: "sm" })}
                >
                  <Plus className="size-4" />
                  Create Event
                </Link>
              }
            />
          ) : null}

          <div className="grid gap-2">
            {pageItems.map((event) => (
              <EventListRow key={event.id} event={event} />
            ))}
          </div>

          <EventListPagination
            safePage={safePage}
            pageCount={pageCount}
            totalCount={pageQuery.data?.total ?? 0}
            onNavigate={handleNavigate}
          />
        </CardContent>
      </Card>
    </div>
  )
}

const deriveMetrics = (events: GuildEventRecord[]) => {
  const nowMs = Date.now()
  let upcoming = 0
  let scheduled = 0
  let degraded = 0

  for (const event of events) {
    if (new Date(event.startAt).getTime() >= nowMs) {
      upcoming += 1
    }

    if (event.status === "scheduled") {
      scheduled += 1
    }

    if (event.syncStatus === "degraded") {
      degraded += 1
    }
  }

  return { upcoming, scheduled, degraded }
}
